import classNames from "classnames";
import * as React from "react";
import GlueFrame from "../../../vendor/glueframelib";

export interface VideoPlayerProps {
    url: string;
    autoPlay?: boolean;
    autoMute?: boolean;
    hideNativePlayBtn?: boolean;
    loop?: boolean;
    playerId?: string;
    setDuration?: (duration: number) => void;
    setIsPlaying?: (isPlaying: boolean) => void;
    isPlaying?: boolean;
}

const VideoPlayer = ({
    url,
    autoPlay,
    autoMute,
    hideNativePlayBtn,
    loop,
    playerId,
    setDuration,
    setIsPlaying,
    isPlaying
}: VideoPlayerProps) => {
    const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
    const iframe = React.useRef<HTMLIFrameElement>(null);
    const player = React.useRef<any>(null);

    React.useLayoutEffect(() => {
        if (iframe.current) {
            const glueFrame = new (GlueFrame as any)(iframe.current, "Player");

            glueFrame.bind("player:video:loaded", () => {
                setIsVideoLoaded(true);
                if (setDuration) {
                    glueFrame.get("duration", (duration?: number) => {
                        if (duration) {
                            setDuration(duration);
                        }
                    });
                }
            });

            if (setIsPlaying) {
                glueFrame.bind("player:video:play", () => setIsPlaying(true));
                glueFrame.bind("player:video:pause", () => setIsPlaying(false));
                glueFrame.bind("player:video:end", () => setIsPlaying(false));
            }

            player.current = glueFrame;
        }

        return () => player.current?.destroy();
    }, [iframe]);

    React.useEffect(() => {
        player.current?.set("playing", isPlaying);
    }, [isPlaying]);

    const src = React.useMemo(() => {
        const splitter = url.includes("/video/") ? "/video/" : "/secret/";
        const [hostName, trailing] = url.split(splitter);
        const [videoId, videoTokenId] = trailing.split("/");
        const domain = `${hostName}/${playerId || "v"}.ihtml/player.html`;
        const video = `photo%5fid=${videoId}`;
        return !!videoTokenId
            ? `${domain}?token=${videoTokenId}&source=embed&${video}&autoPlay=${autoPlay ? 1 : 0}&autoMute=${
                  autoMute ? 1 : 0
              }&loop=${loop ? 1 : 0}&hideBigPlay=${hideNativePlayBtn ? 1 : 0}`
            : `${domain}?source=site&${video}&autoPlay=${autoPlay ? 1 : 0}&autoMute=${
                  autoMute ? 1 : 0
              }&loop=${loop ? 1 : 0}&hideBigPlay=${hideNativePlayBtn ? 1 : 0}`;
    }, [url]);

    const baseClass = "video-player";
    const componentClasses = classNames(baseClass, { [`${baseClass}--has-loaded`]: isVideoLoaded });

    return (
        <div className={componentClasses}>
            <iframe
                loading="lazy"
                ref={iframe}
                src={src}
                scrolling="no"
                frameBorder={0}
                allow="fullscreen; autoplay"
                allowFullScreen
            />
        </div>
    );
};

export default VideoPlayer;
